import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  AdminPageHeader,
  ButtonType,
  Card,
  HeaderStyle,
  IconButtonProps,
  IconStyle,
  Theme,
  TableContainer,
} from '../../../../ui/components';
import { convertGMT8date, dateTimeToCustomFormat, LoggerService } from '../../../../ui/utils';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { DynamicObjectInterface } from '../../../../sdk/type';
import CoachService from '../../../../sdk/services/coachService';
import RewardService from '../../../../sdk/services/rewardService';
import AuditLogService from '../../../../sdk/services/auditLogService';
import { useAPICall } from '../../../../hooks';
import { getRewardListConfig, getTeamListConfig, CoachData, MasterData } from '../coachConfig';
import AuditHistoryAccordion from '../../audit-log/auditLogAccordion';
import styles from '../index.module.scss';
import { PointAllocationModal, RewardGiftModal } from './editModal';

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  ParticipantDetails: 'participantDetails',
  PointAllocation: 'pointAllocation',
  RewardGift: 'rewardGift',
} as const;

interface SearchParams {
  campaignId: string;
  recId: string;
}

export const AdminGeneralCoachDetails = () => {
  const [data, setData] = useState<CoachData | null>(null);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);
  const [displayAuditEmpty, setAuditDisplayEmpty] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let keys: SearchParams = {
      campaignId: params.get('campaignId') ?? '',
      recId: params.get('') ?? '',
    };
    setSearchParams(keys);
  }, [location]);

  useEffect(() => {
    if (searchParams) {
      fetchCoachData(searchParams.recId);
      fetchMasterData();
    }
  }, [searchParams]);

  const fetchCoachData = async (id: string) => {
    let res = await apiCall(CoachService?.getCoachDetailByCampaignId, `${id}`);
    let coachData = {
      ...res?.data?.data?.[0],
      rewards: res?.data?.data?.[0].rewards.map((r: DynamicObjectInterface) => {
        return {
          ...r,
          rewardStatus: r.rewardStatus && r.rewardStatus.charAt(0).toUpperCase() + r.rewardStatus.slice(1),
          rewardModifiedDate: r?.rewardModifiedDate ? new Date(convertGMT8date(r?.rewardModifiedDate)) : undefined,
        };
      }),
    };
    setData(coachData);
  };

  const fetchMasterData = async () => {
    let list = {
      reward: {
        endpoint: RewardService.getRewardList,
      },
    };
    try {
      const results = await Promise.all(Object.values(list).map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data;
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
    }
  };

  const onBackButtonClick = () => {
    // navigate(
    //   searchParams?.campaignId
    //     ? `/admin/manage-coach/list?campaignId=${searchParams?.campaignId}`
    //     : `/admin/manage-coach/list`,
    // );
    navigate(-1);
  };

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getButtonConfig = useMemo(() => {
    let commonConfig = {
      iconName: 'pencil' as IconName,
      iconStyle: IconStyle.SOLID,
      theme: Theme.WHITE,
      title: t('admin.participant.edit'),
    };
    return {
      participantDetails: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.ParticipantDetails) }],
      pointAllocation: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.PointAllocation) }],
      rewardGift: [{ ...commonConfig, onClick: () => toggleEditModal(Sections.RewardGift) }],
    } as { [index: string]: IconButtonProps[] };
  }, []);

  const onEdit = (data: DynamicObjectInterface) => {
    sessionStorage.setItem('redirectFrom', window.location.pathname + window.location.search);
    navigate(`/admin/challenge/manage-team/edit?=${data?.teamId}`);
  };

  const onSaveCoachDetails = async (formData: CoachData) => {
    const activeSection = Object.entries(showEdit).find(entry => entry[1] === true)?.[0] as
      | (typeof Sections)[keyof typeof Sections]
      | undefined;

    const updatedFormData = {
      ...formData,
      section: activeSection,
      rewards: formData?.rewards
        ?.map((reward: DynamicObjectInterface) => {
          reward.rewardRecId = reward.rewardRecId?.toString()?.includes('NEW') ? null : reward.rewardRecId;
          return { ...reward };
        })
        .filter(reward => !(!reward.rewardRecId && reward.rewardStatus === 'REMOVED')),
    };
    const result = await apiCall(CoachService.updateTeamCoachDetails, formData.recId?.toString(), updatedFormData);

    if (result.status === 200) {
      adminToast({
        message: t('admin.participant.updateSuccess'),
        theme: Theme.SUCCESS,
      });
      await fetchCoachData(searchParams?.recId ?? '');
      toggleEditModal(activeSection as (typeof Sections)[keyof typeof Sections]);
    } else {
      adminToast({
        message: t('admin.participant.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const showModal = (
    <>
      {showEdit.pointAllocation && (
        <PointAllocationModal
          data={data}
          onSave={onSaveCoachDetails}
          onCancel={() => toggleEditModal(Sections.PointAllocation)}
        />
      )}
      {showEdit.rewardGift && (
        <RewardGiftModal
          data={data}
          onSave={onSaveCoachDetails}
          onCancel={() => toggleEditModal(Sections.RewardGift)}
          masterData={masterData}
        />
      )}
    </>
  );

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        if (searchParams?.campaignId && data?.coachId) {
          const payload = {
            campaignId: searchParams?.campaignId,
            coachId: data?.coachId,
          };

          const historyLogResponse = await apiCall(AuditLogService.fetchLog, payload);
          const historyLogResult = historyLogResponse.data;

          setAuditHistoryLog(historyLogResult.data || []);
          setAuditDisplayEmpty(historyLogResult.data.length === 0);
        }
      } catch (error) {
        LoggerService.log('Failed to retrieve audit history log', error);
      }
    };

    fetchAuditLogs();
  }, [searchParams?.campaignId, data?.coachId]);

  return (
    <div className={styles['coach-details']}>
      <AdminPageHeader withBackButton onBackButtonClick={onBackButtonClick} title={t('admin.teams.coach')} />
      <div className={styles['coach-details-spacing-v']} style={{ marginTop: '0.5rem' }}>
        <Card title={`Details`} headerStyle={HeaderStyle.HEADER_5} buttonType={ButtonType.ICON}>
          <div className={styles['details']}>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.coachEnrollmentMaster.campaignName')}</div>
                <p>{data?.campaignName ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.coachEnrollmentMaster.coachId')}</div>
                <p>{data?.coachId ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Enrollment Period</div>
                <p>
                  {data?.enrollmentDate
                    ? dateTimeToCustomFormat(data?.enrollmentDate?.toString() ?? '', 'MMM YYYY') +
                      ' - ' +
                      dateTimeToCustomFormat(data?.endDate?.toString() ?? '', 'MMM YYYY')
                    : '-'}
                </p>
              </div>
            </div>
            <div className={styles['details-info']}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.coachEnrollmentMaster.coachAboId')}</div>
                <p>{data?.aboId ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.coachEnrollmentMaster.coachName')}</div>
                <p>{data?.name ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>{t('admin.coachEnrollmentMaster.coachPin')}</div>
                <p>{data?.pin ?? '-'}</p>
              </div>
            </div>
            {/* <div className={`${styles['details-info']} ${styles['details-info-direct-leader']}`}>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Direct Leader ABO ID</div>
                <p>{data?.directLeader ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Direct Leader Name</div>
                <p>{data?.directLeaderName ?? '-'}</p>
              </div>
              <div className={styles['details-info-item']}>
                <div className={styles['label']}>Direct Leader Pin</div>
                <p>{data?.directLeaderPin ?? '-'}</p>
              </div>
            </div> */}
          </div>
        </Card>
        <div className={styles['layout']}>
          <Card
            title={`Achievement Summary`}
            headerStyle={HeaderStyle.HEADER_5}
            // buttonType={ButtonType.ICON}
            // button={getButtonConfig.pointAllocation}
          >
            <div className={styles['allocation']}>
              <div className={styles['allocation-container']}>
                <div className={styles['allocation-container-item']}>
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>{t('admin.generalCoach.totalTeams')}</div>
                    <div className={styles['allocation-total-number']}>{data?.totalTeams ?? 0}</div>
                  </div>
                  <div className={styles['allocation-total-number']}></div>
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>{t('admin.generalCoach.totalTeamAchieved')}</div>
                    <div className={styles['allocation-total-number']}>{data?.totalTeamAchieved ?? 0}</div>
                  </div>
                </div>
              </div>
              {/* <hr className={styles['horizontal-divider']} /> 
              <div className={styles['allocation-container']}>
                <div className={styles['allocation-container-item']}>
                  <div className={`${styles['allocation-total']} ${styles['allocation-total']}`}>
                    <div className={styles['allocation-total-title']}>Personal Sponsor</div>
                    <div className={styles['allocation-total-number']}>{data?.newAboCount?.point ?? 0}</div>
                    <span>
                      Last Update:{' '}
                      {data?.newAboCount?.lastUpdateDate
                        ? `${formatDateDMYYYY(UTCToLocal(data?.newAboCount?.lastUpdateDate))}`
                        : '-'}
                    </span>
                  </div>
                  <div className={styles['allocation-total-number']}>+</div>
                  <div className={styles['allocation-adj']}>
                    <div className={styles['allocation-total-number']}>{data?.newAboCount?.pointAdj ?? 0}</div>
                  </div>
                  <div className={styles['allocation-total-number']}>=</div>
                  <div className={styles['allocation-total']}>
                    <div className={styles['allocation-total-title']}>Total</div>
                    <div className={styles['allocation-total-number']}>
                      {parseFloat(data?.newAboCount?.point ?? '0') + parseFloat(data?.newAboCount?.pointAdj ?? '0')}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          </Card>
          <Card
            title={t('admin.participant.rewardTitle')}
            headerStyle={HeaderStyle.HEADER_5}
            buttonType={ButtonType.ICON}
            button={getButtonConfig.rewardGift}
          >
            <TableContainer
              dataSource={data?.rewards ?? []}
              getColumns={getRewardListConfig}
              pagination={false}
              rowKey="rewardRecId"
            />
          </Card>
        </div>
        <Card title={`Team`} headerStyle={HeaderStyle.HEADER_5} buttonType={ButtonType.ICON}>
          <TableContainer
            dataSource={data?.teams ?? []}
            getColumns={getTeamListConfig}
            pagination={false}
            rowKey="teamId"
            cb={{ onEdit }}
          />
        </Card>
      </div>
      {showModal}
      <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={displayAuditEmpty} />
    </div>
  );
};
